import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { List } from '@refinedev/antd'
import { useGetIdentity } from '@refinedev/core'
import {
  Table,
  Tag,
  Button,
  Tabs,
  Alert,
  Space,
  Typography,
  Avatar,
  Spin,
  Empty,
  Badge as AntBadge
} from 'antd'
import {
  ExperimentOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { StaffRole } from '@/lib/permissions'

const { Text } = Typography

interface UserIdentity {
  id: string
  name: string
  email: string
  role: StaffRole
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('admin_access_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  }
}

type LabResultStatus =
  | 'PENDING'
  | 'RECEIVED'
  | 'VERIFIED'
  | 'AWAITING_REVIEW'
  | 'APPROVED'
  | 'REJECTED'
  | 'SHARED_WITH_PATIENT'

type LabResultSource = 'AQUALAB_EMAIL' | 'PATIENT_UPLOAD' | 'NURSE_MANUAL'

interface LabResultItem {
  id: string
  labName: string
  sampleDate: string
  status: LabResultStatus
  source: LabResultSource
  createdAt: string
  hoursWaiting?: number
  isOverdue?: boolean
  user: {
    id: string
    email: string
    firstName: string | null
    lastName: string | null
    image: string | null
  }
  assignedDoctor?: {
    id: string
    user: {
      firstName: string
      lastName: string
    }
  } | null
}

interface QueueResponse {
  data: LabResultItem[]
  total: number
  page: number
  pageSize: number
  pageCount: number
}

interface QueueCounts {
  nurseQueue: number
  awaitingReview: number
  approved: number
  rejected: number
  shared: number
  total: number
}

type TabType = 'queue' | 'review' | 'approved' | 'rejected' | 'shared' | 'all'

const STATUS_CONFIG: Record<LabResultStatus, { color: string; text: string }> = {
  PENDING: { color: 'default', text: 'Na cekanju' },
  RECEIVED: { color: 'processing', text: 'Primljeno' },
  VERIFIED: { color: 'purple', text: 'Verifikovano' },
  AWAITING_REVIEW: { color: 'warning', text: 'Ceka pregled' },
  APPROVED: { color: 'success', text: 'Odobreno' },
  REJECTED: { color: 'error', text: 'Odbijeno' },
  SHARED_WITH_PATIENT: { color: 'success', text: 'Deljeno' },
}

const SOURCE_LABELS: Record<LabResultSource, string> = {
  AQUALAB_EMAIL: 'AquaLab',
  PATIENT_UPLOAD: 'Pacijent',
  NURSE_MANUAL: 'Rucno',
}

function WaitingTimeBadge({ hours, isOverdue }: { hours: number; isOverdue: boolean }) {
  if (isOverdue) {
    return (
      <Tag color="error" icon={<ClockCircleOutlined />}>
        {hours}h - Kasni!
      </Tag>
    )
  }
  if (hours >= 24) {
    return (
      <Tag color="warning" icon={<ClockCircleOutlined />}>
        {hours}h
      </Tag>
    )
  }
  return (
    <Tag icon={<ClockCircleOutlined />}>
      {hours}h
    </Tag>
  )
}

export function LabResultsListPage() {
  const { data: identity } = useGetIdentity<UserIdentity>()
  const [data, setData] = useState<LabResultItem[]>([])
  const [counts, setCounts] = useState<QueueCounts | null>(null)
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  // Doctors see 'review' tab by default, nurses/admins see 'queue'
  const getDefaultTab = (): TabType => {
    if (identity?.role === 'DOCTOR') return 'review'
    return 'queue'
  }
  const [activeTab, setActiveTab] = useState<TabType | null>(null)
  const pageSize = 10

  // Set default tab based on role once identity loads
  useEffect(() => {
    if (identity && activeTab === null) {
      setActiveTab(getDefaultTab())
    }
  }, [identity])

  const fetchCounts = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/lab-results/counts`, {
        headers: getAuthHeaders(),
      })
      if (!res.ok) throw new Error('Failed to fetch counts')
      const json: QueueCounts = await res.json()
      setCounts(json)
    } catch (error) {
      console.error('Error fetching counts:', error)
    }
  }

  const fetchData = async (page: number, tab: TabType) => {
    setIsLoading(true)
    setIsError(false)
    try {
      let url = `${API_URL}/admin/lab-results`

      if (tab === 'queue') {
        url = `${API_URL}/admin/lab-results/queue`
      } else if (tab === 'review') {
        url = `${API_URL}/admin/lab-results?status=AWAITING_REVIEW`
      } else if (tab === 'approved') {
        url = `${API_URL}/admin/lab-results?status=APPROVED`
      } else if (tab === 'rejected') {
        url = `${API_URL}/admin/lab-results?status=REJECTED`
      } else if (tab === 'shared') {
        url = `${API_URL}/admin/lab-results?status=SHARED_WITH_PATIENT`
      }

      url += url.includes('?') ? '&' : '?'
      url += `page=${page}&pageSize=${pageSize}`

      const res = await fetch(url, { headers: getAuthHeaders() })
      if (!res.ok) {
        const errorBody = await res.text().catch(() => 'No body')
        console.error(`Lab results fetch failed: ${res.status} ${res.statusText}`, errorBody)
        throw new Error(`Failed to fetch: ${res.status} ${res.statusText}`)
      }
      const json: QueueResponse = await res.json()
      setData(json.data)
      setTotal(json.total)
    } catch (error) {
      console.error('Error fetching lab results:', error)
      setIsError(true)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchCounts()
  }, [])

  useEffect(() => {
    if (activeTab) {
      setCurrentPage(1)
      fetchData(1, activeTab)
    }
  }, [activeTab])

  useEffect(() => {
    if (activeTab) {
      fetchData(currentPage, activeTab)
    }
  }, [currentPage])

  // Show loading while waiting for identity
  const isNurseOrAdmin = identity?.role === 'NURSE' || identity?.role === 'ADMIN'

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('sr-Latn-RS', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const getPatientName = (user: LabResultItem['user']) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim()
    }
    return user.email
  }

  const tabItems = [
    // Queue tab only for nurses/admins
    ...(isNurseOrAdmin ? [{
      key: 'queue',
      label: (
        <Space>
          Ceka verifikaciju
          {counts?.nurseQueue ? <AntBadge count={counts.nurseQueue} style={{ backgroundColor: '#1677ff' }} /> : null}
        </Space>
      )
    }] : []),
    {
      key: 'review',
      label: (
        <Space>
          Kod lekara
          {counts?.awaitingReview ? <AntBadge count={counts.awaitingReview} /> : null}
        </Space>
      )
    },
    {
      key: 'approved',
      label: (
        <Space>
          Odobreno
          {counts?.approved ? <AntBadge count={counts.approved} style={{ backgroundColor: '#52c41a' }} /> : null}
        </Space>
      )
    },
    {
      key: 'rejected',
      label: (
        <Space>
          Odbijeno
          {counts?.rejected ? <AntBadge count={counts.rejected} style={{ backgroundColor: '#ff4d4f' }} /> : null}
        </Space>
      )
    },
    {
      key: 'shared',
      label: (
        <Space>
          Deljeno
          {counts?.shared ? <AntBadge count={counts.shared} style={{ backgroundColor: '#52c41a' }} /> : null}
        </Space>
      )
    },
    {
      key: 'all',
      label: (
        <Space>
          Svi
          {counts?.total ? <AntBadge count={counts.total} style={{ backgroundColor: '#8c8c8c' }} /> : null}
        </Space>
      )
    },
  ]

  const columns = [
    {
      title: 'Pacijent',
      dataIndex: 'user',
      key: 'patient',
      render: (_: unknown, record: LabResultItem) => (
        <Space>
          <Avatar
            src={record.user.image}
            icon={!record.user.image && <UserOutlined />}
            style={{ backgroundColor: '#1677ff' }}
          />
          <Link to={`/lab-results/${record.id}`}>
            <Text strong style={{ color: '#1677ff', cursor: 'pointer' }}>
              {getPatientName(record.user)}
            </Text>
          </Link>
        </Space>
      ),
    },
    {
      title: 'Laboratorija',
      dataIndex: 'labName',
      key: 'labName',
    },
    {
      title: 'Datum uzorka',
      dataIndex: 'sampleDate',
      key: 'sampleDate',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Izvor',
      dataIndex: 'source',
      key: 'source',
      render: (source: LabResultSource) => (
        <Tag>{SOURCE_LABELS[source]}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: LabResultStatus) => {
        const config = STATUS_CONFIG[status]
        return <Tag color={config.color}>{config.text}</Tag>
      },
    },
    ...(activeTab === 'queue' ? [{
      title: 'Ceka',
      key: 'waiting',
      render: (_: unknown, record: LabResultItem) =>
        record.hoursWaiting !== undefined ? (
          <WaitingTimeBadge hours={record.hoursWaiting} isOverdue={record.isOverdue || false} />
        ) : null,
    }] : []),
    ...((activeTab === 'review' || activeTab === 'all') ? [{
      title: 'Lekar',
      key: 'doctor',
      render: (_: unknown, record: LabResultItem) =>
        record.assignedDoctor ? (
          <Text>Dr. {record.assignedDoctor.user.firstName} {record.assignedDoctor.user.lastName}</Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    }] : []),
    {
      title: 'Akcija',
      key: 'action',
      width: 120,
      render: (_: unknown, record: LabResultItem) => (
        activeTab === 'queue' ? (
          <Link to={`/lab-results/${record.id}`}>
            <Button type="primary" style={{ backgroundColor: '#13c2c2' }}>
              Verifikuj
            </Button>
          </Link>
        ) : (
          <Link to={`/lab-results/${record.id}`}>
            <Button>Detalji</Button>
          </Link>
        )
      ),
    },
  ]

  // Wait for identity to load and set default tab
  if (!activeTab) {
    return (
      <List title="Lab rezultati">
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </List>
    )
  }

  if (isError) {
    return (
      <List title="Lab rezultati">
        <Alert
          message="Greška pri učitavanju"
          description="Greška pri učitavanju lab rezultata. Proverite da li je API pokrenut i da li ste ulogovani. Pogledajte browser konzolu za više detalja."
          type="error"
          showIcon
          action={
            <Button onClick={() => {
              setIsError(false)
              fetchData(currentPage, activeTab)
            }}>
              Pokušaj ponovo
            </Button>
          }
        />
      </List>
    )
  }

  return (
    <List
      title="Lab rezultati"
      headerButtons={
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            fetchCounts()
            fetchData(currentPage, activeTab)
          }}
        >
          Osvezi
        </Button>
      }
    >
      {/* Info Banner for Queue (nurses/admins only) */}
      {activeTab === 'queue' && isNurseOrAdmin && (
        <Alert
          message="Red za verifikaciju"
          description="Ovi rezultati cekaju da se verifikuju vrednosti biomarkera. Kliknite na red da pregledate detalje i verifikujete rezultate."
          type="info"
          icon={<ExperimentOutlined />}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Info Banner for Doctor Review */}
      {activeTab === 'review' && identity?.role === 'DOCTOR' && (
        <Alert
          message="Rezultati za pregled"
          description="Ovi rezultati su dodeljeni vama na pregled. Pregledajte vrednosti i ostavite beleške za pacijenta."
          type="info"
          icon={<ExperimentOutlined />}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as TabType)}
        items={tabItems}
        style={{ marginBottom: 16 }}
      />

      {/* Table */}
      <Spin spinning={isLoading}>
        <Table
          dataSource={data}
          columns={columns}
          rowKey="id"
          scroll={{ x: 800 }}
          locale={{
            emptyText: (
              <Empty
                image={<ExperimentOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />}
                description="Nema lab rezultata u ovoj kategoriji"
              />
            ),
          }}
          pagination={{
            current: currentPage,
            pageSize,
            total,
            onChange: (page) => setCurrentPage(page),
            showTotal: (total, range) => `Prikazano ${range[0]}-${range[1]} od ${total} rezultata`,
            showSizeChanger: false,
          }}
        />
      </Spin>
    </List>
  )
}

export { default as LabResultShowPage } from './show'
